import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaSearch, FaTimes, FaHardHat, FaFire, FaUsers, FaCogs, FaBolt,
  FaBuilding, FaFirstAid, FaFileAlt, FaClipboardList, FaStream,
  FaEye, FaCog, FaUserTie, FaChevronRight, FaMapMarkerAlt,
  FaShieldAlt, FaHistory, FaKeyboard
} from 'react-icons/fa';
import { getApiEndpoint } from '../../../config/api';
import './PropertySearch.css';

/* ── Section icon + colour map ──────────────────────────────── */
const SECTION_META = {
  overview:    { icon: FaEye,          color: '#0d9488', bg: '#f0fdfa', label: 'Overview' },
  roof:        { icon: FaHardHat,      color: '#1A80E5', bg: '#EBF4FF', label: 'Roof Safety' },
  fire:        { icon: FaFire,         color: '#ef4444', bg: '#fef2f2', label: 'Fire Safety' },
  staff:       { icon: FaUsers,        color: '#8b5cf6', bg: '#f5f3ff', label: 'Staff Safety' },
  equipment:   { icon: FaCogs,         color: '#d97706', bg: '#fffbeb', label: 'Equipment Safety' },
  electrical:  { icon: FaBolt,         color: '#d97706', bg: '#fffbeb', label: 'Electrical Safety' },
  building:    { icon: FaBuilding,     color: '#0d9488', bg: '#f0fdfa', label: 'Building Maintenance' },
  emergency:   { icon: FaFirstAid,     color: '#ef4444', bg: '#fef2f2', label: 'Emergency Preparedness' },
  documents:   { icon: FaFileAlt,      color: '#6366f1', bg: '#eef2ff', label: 'Documents' },
  inspections: { icon: FaClipboardList, color: '#0d9488', bg: '#f0fdfa', label: 'Inspections' },
  activity:    { icon: FaStream,       color: '#6b7280', bg: '#f3f4f6', label: 'Activity' },
  logs:        { icon: FaHistory,      color: '#6b7280', bg: '#f3f4f6', label: 'Activity Logs' },
  management:  { icon: FaUserTie,      color: '#0d9488', bg: '#f0fdfa', label: 'View Management' },
  settings:    { icon: FaCog,          color: '#6b7280', bg: '#f3f4f6', label: 'Settings' },
};

/* ── Status badge colour ────────────────────────────────────── */
const STATUS_COLORS = {
  Pass:     { bg: '#f0fdf4', color: '#16a34a' },
  Passed:   { bg: '#f0fdf4', color: '#16a34a' },
  Active:   { bg: '#f0fdf4', color: '#16a34a' },
  Fail:     { bg: '#fef2f2', color: '#dc2626' },
  Failed:   { bg: '#fef2f2', color: '#dc2626' },
  Expired:  { bg: '#fef2f2', color: '#dc2626' },
  Pending:  { bg: '#fffbeb', color: '#d97706' },
  Overdue:  { bg: '#fef2f2', color: '#dc2626' },
  Unknown:  { bg: '#f3f4f6', color: '#6b7280' },
};

/* ── All navigable pages ────────────────────────────────────── */
const ALL_PAGES = [
  { id: 'overview',    label: 'Overview',                section: 'overview',    keywords: ['home', 'dashboard', 'summary', 'overview'] },
  { id: 'roof',        label: 'Roof Safety',             section: 'roof',        keywords: ['roof', 'anchor', 'cradle', 'ladder', 'walkway', 'abseil', 'static line', 'handrail', 'height', 'fall'] },
  { id: 'fire',        label: 'Fire Safety',             section: 'fire',        keywords: ['fire', 'extinguisher', 'smoke', 'alarm', 'sprinkler', 'emergency light', 'fire door', 'heat'] },
  { id: 'staff',       label: 'Staff Safety',            section: 'staff',       keywords: ['staff', 'team', 'employee', 'training', 'certification', 'people', 'hr'] },
  { id: 'equipment',   label: 'Equipment Safety',        section: 'equipment',   keywords: ['equipment', 'lift', 'escalator', 'crane', 'hoist', 'machinery', 'fixed'] },
  { id: 'electrical',  label: 'Electrical Safety',       section: 'electrical',  keywords: ['electrical', 'pat', 'rcd', 'wiring', 'circuit', 'power'] },
  { id: 'building',    label: 'Building Maintenance',    section: 'building',    keywords: ['building', 'hvac', 'plumbing', 'facilities', 'maintenance', 'repair'] },
  { id: 'emergency',   label: 'Emergency Preparedness',  section: 'emergency',   keywords: ['emergency', 'evacuation', 'assembly', 'first aid', 'preparedness'] },
  { id: 'documents',   label: 'Documents',               section: 'documents',   keywords: ['document', 'file', 'upload', 'certificate', 'report', 'insurance', 'pdf'] },
  { id: 'inspections', label: 'Inspections',             section: 'inspections', keywords: ['inspection', 'test', 'check', 'audit', 'review', 'examine'] },
  { id: 'activity',    label: 'Activity',                section: 'activity',    keywords: ['activity', 'log', 'history', 'audit trail', 'event', 'action'] },
  { id: 'logs',        label: 'Activity Logs',           section: 'logs',        keywords: ['log', 'history', 'audit', 'trail', 'event'] },
  { id: 'management',  label: 'View Management',         section: 'management',  keywords: ['management', 'manager', 'building manager', 'assign'] },
  { id: 'settings',    label: 'Settings',                section: 'settings',    keywords: ['settings', 'config', 'preferences', 'options'] },
];

/* Utility sections every property user can search (not role-gated) */
const ALWAYS_SEARCHABLE = ['documents', 'inspections', 'activity', 'logs', 'overview'];

export default function PropertySearch({ isOpen, onClose, propertyId, allowedSections }) {
  const [query, setQuery] = useState('');
  const [apiResults, setApiResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Determine base path
  const basePath = useMemo(() => {
    if (!propertyId) return null;
    return location.pathname.startsWith('/properties/')
      ? `/properties/${propertyId}`
      : `/propertydashboard/${propertyId}`;
  }, [propertyId, location.pathname]);

  // Filter pages by allowed sections (utility sections always accessible)
  const accessiblePages = useMemo(() => {
    if (!allowedSections || allowedSections.length === 0) return ALL_PAGES;
    const visible = new Set([...allowedSections, ...ALWAYS_SEARCHABLE]);
    return ALL_PAGES.filter(p => visible.has(p.section));
  }, [allowedSections]);

  // Client-side page matching
  const pageResults = useMemo(() => {
    if (!query.trim()) return accessiblePages.slice(0, 6);
    const q = query.toLowerCase();
    return accessiblePages.filter(p =>
      p.label.toLowerCase().includes(q) ||
      p.keywords.some(k => k.includes(q))
    );
  }, [query, accessiblePages]);

  // Debounced API search
  const searchApi = useCallback(async (term) => {
    if (!propertyId || term.length < 2) {
      setApiResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const sectionsParam = allowedSections && allowedSections.length > 0
        ? `&sections=${[...new Set([...allowedSections, ...ALWAYS_SEARCHABLE])].join(',')}`
        : '';
      const res = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/search?q=${encodeURIComponent(term)}${sectionsParam}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setApiResults(data.results || []);
      } else {
        setApiResults([]);
      }
    } catch {
      setApiResults([]);
    } finally {
      setLoading(false);
    }
  }, [propertyId, allowedSections]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length >= 2) {
      setLoading(true);
      debounceRef.current = setTimeout(() => searchApi(query.trim()), 300);
    } else {
      setApiResults([]);
      setLoading(false);
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, searchApi]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setApiResults([]);
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Group API results by category
  const groupedApiResults = useMemo(() => {
    const groups = {};
    apiResults.forEach(r => {
      if (!groups[r.category]) groups[r.category] = [];
      groups[r.category].push(r);
    });
    return groups;
  }, [apiResults]);

  // Build flat list for keyboard navigation
  const allItems = useMemo(() => {
    const items = [];
    // Pages first
    pageResults.forEach(p => items.push({ type: 'page', data: p }));
    // Then API results grouped
    Object.entries(groupedApiResults).forEach(([, results]) => {
      results.forEach(r => items.push({ type: 'result', data: r }));
    });
    return items;
  }, [pageResults, groupedApiResults]);

  // Reset active index when results change
  useEffect(() => { setActiveIndex(0); }, [allItems.length]);

  // Navigate to result
  const navigateToItem = useCallback((item) => {
    if (!basePath) return;
    onClose();
    if (item.type === 'page') {
      const page = item.data;
      const routes = {
        overview: basePath,
        roof: `${basePath}/roof-safety`,
        fire: `${basePath}/fire-safety`,
        staff: `${basePath}/staff-safety`,
        equipment: `${basePath}/equipment-safety`,
        electrical: `${basePath}/electrical-safety`,
        building: `${basePath}/building-maintenance`,
        emergency: `${basePath}/emergency-preparedness`,
        documents: `${basePath}/documents`,
        inspections: `${basePath}/inspections`,
        activity: `${basePath}/activity`,
        logs: `${basePath}/logs`,
        management: `${basePath}/settings/management`,
        settings: `${basePath}/settings`,
      };
      navigate(routes[page.id] || basePath);
    } else {
      const r = item.data;
      const sectionRoutes = {
        roof: `${basePath}/roof-safety`,
        fire: `${basePath}/fire-safety`,
        staff: `${basePath}/staff-safety`,
        equipment: `${basePath}/equipment-safety`,
        electrical: `${basePath}/electrical-safety`,
        building: `${basePath}/building-maintenance`,
        emergency: `${basePath}/emergency-preparedness`,
        documents: `${basePath}/documents`,
        inspections: `${basePath}/inspections`,
        activity: `${basePath}/activity`,
        logs: `${basePath}/logs`,
      };

      // Map asset types to sub-section IDs for direct navigation
      const ASSET_TO_SUB = {
        anchor_point: 'anchorPoints',
        cradle: 'cradles',
        abseil_system: 'abseilSystems',
        ladder: 'ladders',
        handrail: 'handrails',
        static_line: 'staticLines',
        walkway: 'walkways',
      };

      // Asset-list pages listen for openAssetFromHotspot with the raw DB
      // table name (plural). Reuse that event so the detail/history modal
      // opens automatically after navigation.
      const ASSET_TO_TABLE = {
        anchor_point: 'anchor_points',
        cradle: 'cradles',
        abseil_system: 'davit_bases',
        ladder: 'ladders',
        handrail: 'handrails',
        static_line: 'static_lines',
        walkway: 'walkways',
        fixed_equipment: 'fixed_equipment',
      };

      // OPEN THE HISTORY MODAL IMMEDIATELY — don't wait for the underlying
      // asset-list page to mount + fetch. PropertyDashboard renders a
      // top-level AssetDetailModal that listens for this event.
      if (r.assetType) {
        window.dispatchEvent(new CustomEvent('openAssetHistory', {
          detail: {
            asset: {
              id: r.id,
              name: r.name,
              location: r.location,
              status: r.status,
            },
            assetType: r.assetType,
          }
        }));
      }

      const route = sectionRoutes[r.section] || basePath;
      navigate(route);

      // Still tell the destination list page to surface the asset (sub-section
      // + open modal there too, in case the user closes the top-level one).
      if (r.assetType && ASSET_TO_SUB[r.assetType]) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('propertySearchNavigate', {
            detail: { subSection: ASSET_TO_SUB[r.assetType], assetId: r.id }
          }));
          const assetTable = ASSET_TO_TABLE[r.assetType];
          if (assetTable) {
            window.dispatchEvent(new CustomEvent('openAssetFromHotspot', {
              detail: { assetId: r.id, assetTable }
            }));
          }
        }, 150);
      }
    }
  }, [basePath, navigate, onClose]);

  // Keyboard handling
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && allItems[activeIndex]) {
      e.preventDefault();
      navigateToItem(allItems[activeIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('.ps-item-active');
    if (active) active.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // Global keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  let flatIndex = 0;

  return (
    <div className="ps-overlay" onClick={onClose}>
      <div className="ps-modal" onClick={e => e.stopPropagation()}>
        {/* Search input */}
        <div className="ps-input-wrap">
          <FaSearch className="ps-input-icon" />
          <input
            ref={inputRef}
            className="ps-input"
            type="text"
            placeholder="Search pages, assets, documents, tests..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button className="ps-input-clear" onClick={() => setQuery('')} aria-label="Clear">
              <FaTimes />
            </button>
          )}
          <kbd className="ps-kbd">esc</kbd>
        </div>

        {/* Results */}
        <div className="ps-results" ref={listRef}>
          {/* Page results */}
          {pageResults.length > 0 && (
            <div className="ps-group">
              <div className="ps-group-label">
                {query.trim() ? 'Pages' : 'Quick Navigation'}
              </div>
              {pageResults.map(page => {
                const idx = flatIndex++;
                const meta = SECTION_META[page.section] || SECTION_META.overview;
                const Icon = meta.icon;
                return (
                  <button
                    key={`page-${page.id}`}
                    className={`ps-item ${idx === activeIndex ? 'ps-item-active' : ''}`}
                    onClick={() => navigateToItem({ type: 'page', data: page })}
                    onMouseEnter={() => setActiveIndex(idx)}
                  >
                    <span className="ps-item-icon" style={{ background: meta.bg, color: meta.color }}>
                      <Icon />
                    </span>
                    <span className="ps-item-body">
                      <span className="ps-item-name">{page.label}</span>
                      <span className="ps-item-sub">Go to page</span>
                    </span>
                    <FaChevronRight className="ps-item-arrow" />
                  </button>
                );
              })}
            </div>
          )}

          {/* API results by category */}
          {Object.entries(groupedApiResults).map(([category, results]) => {
            const sectionKey = results[0]?.section || 'overview';
            const meta = SECTION_META[sectionKey] || SECTION_META.overview;
            return (
              <div key={category} className="ps-group">
                <div className="ps-group-label">{category}</div>
                {results.map(result => {
                  const idx = flatIndex++;
                  const Icon = meta.icon;
                  const statusStyle = STATUS_COLORS[result.status] || STATUS_COLORS.Unknown;
                  return (
                    <button
                      key={`result-${result.section}-${result.id}-${idx}`}
                      className={`ps-item ${idx === activeIndex ? 'ps-item-active' : ''}`}
                      onClick={() => navigateToItem({ type: 'result', data: result })}
                      onMouseEnter={() => setActiveIndex(idx)}
                    >
                      <span className="ps-item-icon" style={{ background: meta.bg, color: meta.color }}>
                        <Icon />
                      </span>
                      <span className="ps-item-body">
                        <span className="ps-item-name">
                          {result.name}
                          <span className="ps-item-id">#{result.id}</span>
                        </span>
                        <span className="ps-item-sub">
                          {result.subType}
                          {result.location && (
                            <><FaMapMarkerAlt className="ps-item-loc-ico" /> {result.location}</>
                          )}
                        </span>
                      </span>
                      {result.status && (
                        <span className="ps-item-status" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                          {result.status}
                        </span>
                      )}
                      <FaChevronRight className="ps-item-arrow" />
                    </button>
                  );
                })}
              </div>
            );
          })}

          {/* Loading */}
          {loading && query.trim().length >= 2 && (
            <div className="ps-loading">
              <div className="ps-loading-dots">
                <span /><span /><span />
              </div>
              <span>Searching property...</span>
            </div>
          )}

          {/* No results */}
          {!loading && query.trim().length >= 2 && pageResults.length === 0 && apiResults.length === 0 && (
            <div className="ps-empty">
              <FaSearch className="ps-empty-icon" />
              <span>No results for "{query}"</span>
              <span className="ps-empty-hint">Try a different search term</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="ps-footer">
          <span className="ps-footer-hint">
            <FaKeyboard className="ps-footer-ico" />
            <kbd className="ps-kbd-sm">&uarr;</kbd>
            <kbd className="ps-kbd-sm">&darr;</kbd>
            to navigate
            <kbd className="ps-kbd-sm">Enter</kbd>
            to select
            <kbd className="ps-kbd-sm">Esc</kbd>
            to close
          </span>
          <span className="ps-footer-badge">
            <FaShieldAlt /> CloudSafe
          </span>
        </div>
      </div>
    </div>
  );
}
